import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isSupabaseConfigured } from '@/config';
import { showRewardedAd } from '@/lib/ads/RewardedAds';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  doubleScoreReward,
  getScorePredictBoard,
  listFixturesForRange,
  listMyScorePredictions,
  submitScorePrediction,
} from '@/lib/data/provider';

/** Past 2 days (for results) + next 8 days (to predict). */
const PAST_DAYS = 2;
const FUTURE_DAYS = 8;

export function useScorePredictFixtures() {
  return useQuery({
    queryKey: ['score-predict', 'fixtures'],
    queryFn: () => {
      const now = Date.now();
      const from = new Date(now - PAST_DAYS * 86400000).toISOString();
      const to = new Date(now + FUTURE_DAYS * 86400000).toISOString();
      return listFixturesForRange(from, to);
    },
    enabled: isSupabaseConfigured,
  });
}

export function useMyScorePredictions() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['score-predict', 'mine', userId ?? 'anon'],
    queryFn: listMyScorePredictions,
    enabled: isSupabaseConfigured && !!userId,
  });
}

export function useSubmitScorePrediction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { fixtureId: string; home: number; away: number }) =>
      submitScorePrediction(v.fixtureId, v.home, v.away),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['score-predict', 'mine'] }),
  });
}

/** Watch a rewarded ad to double a settled prediction's coins (once per pick). */
export function useDoubleScoreReward() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fixtureId: string) => {
      const { earned } = await showRewardedAd();
      if (!earned) throw new Error('AD_NOT_COMPLETED');
      return doubleScoreReward(fixtureId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['score-predict', 'mine'] });
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
    },
  });
}

export function useScorePredictBoard(enabled = true) {
  return useQuery({
    queryKey: ['score-predict', 'board'],
    queryFn: () => getScorePredictBoard(50),
    enabled: isSupabaseConfigured && enabled,
  });
}
